// Product Complementary Manual v2.0 - Updated 2025-11-26
if (!customElements.get('product-complementary-manual')) {
  customElements.define(
    'product-complementary-manual',
    class ProductComplementaryManual extends HTMLElement {
      constructor() {
        super();
        this.init();
      }

      async init() {
        const skusString = this.dataset.skus;
        const skus = skusString.split(',').map(s => s.trim()).filter(Boolean);
        const limit = parseInt(this.dataset.limit) || 10;

        if (skus.length === 0) {
          this.fallbackToRecommendations();
          return;
        }

        try {
          // Initialize empty slider structure immediately
          this.initializeSliderStructure();
          
          // Fetch and add products progressively
          await this.fetchProductCardsProgressively(skus, limit);
        } catch (error) {
          this.fallbackToRecommendations();
        }
      }

      initializeSliderStructure() {
        const layout = this.dataset.layout;
        const columnsDesktop = parseInt(this.dataset.columns) || 3; // Use block setting instead of hardcoded 4
        const columnsMobile = parseInt(this.dataset.columnsMobile) || 2;
        const useOptionalProduct = this.dataset.useOptionalProduct === 'true';
        const heading = useOptionalProduct && this.dataset.optionalHeading 
          ? this.dataset.optionalHeading 
          : this.dataset.heading;
        const headingSize = this.dataset.headingSize || 'h4';
        const makeCollapsible = this.dataset.makeCollapsible === 'true';
        const icon = this.dataset.icon;
        const sectionId = this.dataset.sectionId;
        const blockId = this.dataset.blockId;
        const isListLayout = this.dataset.isListLayout === 'true';

        let html = '<aside aria-label="Complementary products"><div class="complementary-products__container relative">';

        if (makeCollapsible) {
          html += `
            <details id="Details-${blockId}-${sectionId}" class="accordion-details" is="accordion-group" open>
              <summary class="accordion-details__summary flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <h2 class="${headingSize}">${heading}</h2>
                </div>
                <span class="icon-plus-toggle"></span>
              </summary>
              <div class="accordion-details__content">
          `;
        } else if (heading) {
          html += `<h2 class="${headingSize} complementary-products__heading">${heading}</h2>`;
        }

        html += `
          <complementary-product-slider
            class="block${isListLayout ? '' : ' relative swiper-show-nav-on-hover'}"
            data-enable-slider-desktop="true"
            data-enable-slider-mobile="true"
            data-columns-desktop="${isListLayout ? 'auto' : columnsDesktop}"
            data-columns-mobile="${columnsMobile}"
            data-calc-button-position="true"
          >
            <div class="products-wrap swiper${isListLayout ? '' : ' swipe-mobile swipe-mobile--2-cols'}">
              <div class="products f-grid--gap-medium swiper-wrapper${isListLayout ? '' : ' swipe-mobile__inner'}"
                   style="--f-columns-mobile: ${columnsMobile}; --f-columns-md: ${columnsDesktop};">
              </div>
            </div>
        `;

        if (isListLayout) {
          html += `
            <div class="swiper-controls swiper-controls--nav-floating flex">
              <button class="swiper-button swiper-button--drop-shadow swiper-button-prev btn" name="previous" aria-label="Previous slide">
                <span class="btn__text">
                  <svg class="icon icon-caret-left rtl-flip-x icon--small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
              <button class="swiper-button swiper-button--drop-shadow swiper-button-next btn" name="next" aria-label="Next slide">
                <span class="btn__text">
                  <svg class="icon icon-caret-right rtl-flip-x icon--small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>
          `;
        } else {
          html += `
            <div class="swiper-controls swiper-controls--nav-floating flex">
              <button class="swiper-button swiper-button--drop-shadow swiper-button-prev btn" name="previous" aria-label="Previous slide">
                <span class="btn__text">
                  <svg class="icon icon-caret-left rtl-flip-x icon--small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
              <button class="swiper-button swiper-button--drop-shadow swiper-button-next btn" name="next" aria-label="Next slide">
                <span class="btn__text">
                  <svg class="icon icon-caret-right rtl-flip-x icon--small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>
          `;
        }

        html += '</complementary-product-slider>';

        if (makeCollapsible) {
          html += '</div></details>';
        }

        html += '</div></aside>';

        this.innerHTML = html;
        this.productsContainer = this.querySelector('.products');
      }

      async fetchProductCardsProgressively(skus, limit) {
        const productHandles = new Set();
        let addedCount = 0;
        
        for (const sku of skus) {
          if (addedCount >= limit) break;
          
          try {
            const skuTrimmed = sku.trim();
            const searchResponse = await fetch(`/search?q=${encodeURIComponent(skuTrimmed)}&type=product`);
            
            if (searchResponse.ok) {
              const html = await searchResponse.text();
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              
              const productCards = doc.querySelectorAll('.product-card');
              
              for (const card of productCards) {
                const link = card.querySelector('a[href*="/products/"]');
                if (!link) continue;
                
                const href = link.getAttribute('href');
                const match = href.match(/\/products\/([^?#]+)/);
                if (!match || !match[1]) continue;
                
                const handle = match[1];
                if (productHandles.has(handle)) continue;
                
                try {
                  const prodResponse = await fetch(`/products/${handle}.js`);
                  if (prodResponse.ok) {
                    const product = await prodResponse.json();
                    
                    if (product.variants?.some(v => v.sku?.trim() === skuTrimmed)) {
                      productHandles.add(handle);
                      
                      // Clone the entire product card
                      const clonedCard = card.cloneNode(true);
                      
                      // Modify the card to use "+" instead of "Tilføj"
                      this.modifyCardButton(clonedCard);
                      
                      // Add the card immediately
                      this.addProductCard(clonedCard);
                      addedCount++;
                      break;
                    }
                  }
                } catch (e) {
                  // Silent fail
                }
              }
            }
          } catch (e) {
            // Silent fail
          }
        }
        
        if (addedCount === 0) {
          this.fallbackToRecommendations();
        } else {
          // Force slider initialization after products are added
          this.initializeSlider();
        }
      }

      fallbackToRecommendations() {
        const productId = this.dataset.productId;
        const sectionId = this.dataset.sectionId;
        const limit = this.dataset.limit || 10;
        
        // Replace this element with product-recommendations element
        const recommendationsEl = document.createElement('product-recommendations');
        recommendationsEl.className = this.className;
        recommendationsEl.dataset.url = `${window.Shopify?.routes?.root || '/'}recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=complementary`;
        recommendationsEl.dataset.fallbackUrl = `${window.Shopify?.routes?.root || '/'}recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=related`;
        
        // Replace the element - the custom element constructor will automatically call init()
        this.parentNode.replaceChild(recommendationsEl, this);
      }

      initializeSlider() {
        // Wait for all products to be in DOM
        requestAnimationFrame(() => {
          const slider = this.querySelector('complementary-product-slider');
          if (!slider) {
            return;
          }

          // Check if products were added
          const slides = this.querySelectorAll('.swiper-slide');

          if (slides.length === 0) {
            return;
          }

          // Force recalculation of column widths by updating CSS variables
          const productsContainer = this.querySelector('.products');
          if (productsContainer) {
            const columnsDesktop = parseInt(this.dataset.columns) || 3;
            const columnsMobile = parseInt(this.dataset.columnsMobile) || 2;
            productsContainer.style.setProperty('--f-columns-mobile', columnsMobile);
            productsContainer.style.setProperty('--f-columns-md', columnsDesktop);
          }

          // If slider already initialized, just update it
          if (slider.sliderInstance && typeof slider.sliderInstance === 'object') {
            try {
              slider.sliderInstance.slider.update();
              slider.sliderInstance.slider.updateSlides();
              slider.sliderInstance.slider.updateSize();
            } catch (e) {
              // Silent fail
            }
          } else {
            // Initialize slider for the first time
            setTimeout(() => {
              try {
                slider.initSlider();
              } catch (e) {
                // Silent fail
              }
            }, 100);
          }
        });
      }

      modifyCardButton(card) {
        // Check if card has add to cart button
        const atcButton = card.querySelector('.product-card__atc, [data-product-form], form[action*="/cart/add"]');
        
        if (!atcButton) {
          return;
        }

        // Find "Tilføj" button text and replace with "+"
        const buttonTexts = card.querySelectorAll('.product-card__atc-text');
        buttonTexts.forEach(textEl => {
          const text = textEl.textContent.trim();
          if (text.includes('Tilføj') || text.includes('Add to cart') || text === 'Tilføj til kurv') {
            textEl.textContent = '+';
            // Make button smaller
            const btn = textEl.closest('button, a');
            if (btn) {
              btn.style.minWidth = '40px';
              btn.style.padding = '8px';
            }
          }
        });
      }

      addProductCard(card) {
        const wrapper = document.createElement('div');
        wrapper.className = 'f-column product-column swiper-slide';
        wrapper.appendChild(card);
        this.productsContainer.appendChild(wrapper);
      }
    }
  );
}
